import React, { useRef, useState, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SelectInput from "../../../components/SelectInput";
import { formatDateDDMM, getWeekdayShort } from "../../../constants";

// Scrollable pill-tabs for stage switching, falling back to the same
// SelectInput dropdown StageSelector already uses once the pills stop
// fitting in one row - measured via ResizeObserver (scrollWidth vs
// clientWidth) rather than a fixed breakpoint, since the tab count/labels
// vary per tournament (group names, round names, dates...).
const StageTabsScroll = ({ stages, activeStage, onStageChange }) => {
    const { t, i18n } = useTranslation();
    const wrapRef = useRef(null);
    const listRef = useRef(null);
    const [overflowing, setOverflowing] = useState(false);

    // same name-resolution StageSelector uses: title (grid tabs), name
    // (stages), date (schedule) - kept identical so switching a call site
    // between the two components doesn't change what labels show.
    const formattedStages = useMemo(() => stages.map((stage, index) => ({
        id: stage.id,
        name: stage.title || stage.name || (stage.date
            ? `${formatDateDDMM(stage.date)} ${getWeekdayShort(stage.date, i18n.language)}`
            : `${t("Stage")} ${index + 1}`),
    })), [stages, t, i18n.language]);

    useLayoutEffect(() => {
        const wrap = wrapRef.current;
        const list = listRef.current;
        if (!wrap || !list) return;
        const check = () => setOverflowing(list.scrollWidth > wrap.clientWidth + 1);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(wrap);
        return () => ro.disconnect();
    }, [formattedStages]);

    if (stages.length === 0) return null;

    if (overflowing) {
        const activeName = formattedStages.find(s => s.id === activeStage)?.name || t("Select");
        return (
            <li>
                <div className="stage-selector">
                    <div className="stage-absolute-block">
                        <SelectInput
                            data={formattedStages}
                            default_text={activeName}
                            default_value={activeStage}
                            setInputValue={onStageChange}
                            input_name="stage_select"
                            show_label={false}
                            background={true}
                            search={stages.length > 10}
                        />
                    </div>
                </div>
            </li>
        );
    }

    return (
        <li ref={wrapRef} className="stage-tabs-wrap">
            <ul ref={listRef} className="stage-tabs-scroll">
                {formattedStages.map(stage => (
                    <li key={stage.id} className={activeStage === stage.id ? "active" : ""}>
                        <a href="#" onClick={(e) => { e.preventDefault(); onStageChange(stage.id); }}>
                            {stage.name}
                        </a>
                    </li>
                ))}
            </ul>
        </li>
    );
};

export default StageTabsScroll;
